/**
 * GRI Disclosure Detail Modal
 * Inline editing for individual disclosures
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Save, AlertCircle, FileText, Database, Clock } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const GRIDisclosureDetailModal = ({ disclosure, studyId, onClose, onSave }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [formData, setFormData] = useState({
    status: disclosure.status || 'missing',
    narrative: disclosure.narrative || '',
    notes: disclosure.notes || '',
    data: disclosure.data || {}
  });
  const [dataFields, setDataFields] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Parse existing data into editable fields
    if (disclosure.data && typeof disclosure.data === 'object') {
      const fields = Object.entries(disclosure.data).map(([key, value]) => ({
        key,
        value: typeof value === 'object' ? JSON.stringify(value) : value.toString()
      }));
      setDataFields(fields);
    }
  }, [disclosure]);

  const handleAddDataField = () => {
    setDataFields([...dataFields, { key: '', value: '' }]);
  };

  const handleDataFieldChange = (index, field, value) => {
    const updated = [...dataFields];
    updated[index][field] = value;
    setDataFields(updated);
  };

  const handleRemoveDataField = (index) => {
    setDataFields(dataFields.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      // Convert data fields back to object
      const dataObject = {};
      dataFields.forEach(field => {
        if (field.key) {
          // Try to parse as JSON, otherwise keep as string
          try {
            dataObject[field.key] = JSON.parse(field.value);
          } catch {
            dataObject[field.key] = field.value;
          }
        }
      });

      const payload = {
        disclosure_number: disclosure.disclosure_number,
        status: formData.status,
        notes: formData.notes
      };

      await axios.post(
        `${API}/api/gri-v2/${studyId}/update-disclosure-status`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update with full data
      await axios.post(
        `${API}/api/gri-v2/${studyId}/update-disclosure-data`,
        {
          disclosure_number: disclosure.disclosure_number,
          data: dataObject,
          narrative: formData.narrative
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSuccess('Disclosure updated successfully!');
      onSave();
      onClose();
    } catch (error) {
      showError('Error saving disclosure: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              <span className="font-mono text-sm font-semibold">{disclosure.disclosure_number}</span>
            </div>
            <h3 className="text-lg font-semibold mt-1">{disclosure.title}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Status *
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
            >
              <option value="missing">Missing</option>
              <option value="partial">Partial</option>
              <option value="complete">Complete</option>
            </select>
          </div>

          {/* Data Source */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-900 text-sm font-semibold mb-1">
              <Database className="w-4 h-4" />
              Data Source
            </div>
            <p className="text-sm text-blue-700">{disclosure.data_source}</p>
          </div>

          {/* Data Fields */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-semibold text-gray-700">
                Data Fields
              </label>
              <button
                onClick={handleAddDataField}
                className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                + Add Field
              </button>
            </div>
            
            <div className="space-y-2">
              {dataFields.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500 text-sm">No data fields yet. Click "Add Field" to start.</p>
                </div>
              ) : (
                dataFields.map((field, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <input
                      type="text"
                      placeholder="Field name (e.g., total_energy)"
                      value={field.key}
                      onChange={(e) => handleDataFieldChange(index, 'key', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Value"
                      value={field.value}
                      onChange={(e) => handleDataFieldChange(index, 'value', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                    />
                    <button
                      onClick={() => handleRemoveDataField(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Narrative */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Management Approach / Narrative
            </label>
            <textarea
              value={formData.narrative}
              onChange={(e) => setFormData({ ...formData, narrative: e.target.value })}
              placeholder="Describe how your organization addresses this disclosure..."
              rows={6}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              Provide context about policies, processes, and performance related to this disclosure.
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Internal Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any internal notes or comments..."
              rows={3}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none resize-none"
            />
          </div>

          {/* Last Updated */}
          {disclosure.last_updated && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>Last updated: {new Date(disclosure.last_updated).toLocaleString()}</span>
            </div>
          )}

          {/* Help Text */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Tips for completing this disclosure:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Enter quantitative data in the Data Fields section</li>
                  <li>Provide qualitative context in the Narrative section</li>
                  <li>Set status to "Complete" only when all required information is provided</li>
                  <li>Link to evidence documents in the Notes section if applicable</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Disclosure'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GRIDisclosureDetailModal;
